import React from 'react'
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Button,
    Card,
    CardContent,
    Divider
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

const TrainingSetModal = ({ open, onClose, trainingSets, onRemoveTrainingSet, onSave }) => {
    const formatFileName = (fileName) => {
        if (!fileName) return ''
        return fileName.split('/').pop() || fileName
    }

    const handleViewFile = (file) => {
        console.log('View file:', file.name || file)
    }

    const handleReplaceFile = (file) => {
        console.log('Replace file:', file.name || file)
    }

    const handleLearnMore = () => {
        console.log('Learn more clicked')
    }

    const handleRemoveTrainingSet = (trainingSetId) => {
        onRemoveTrainingSet(trainingSetId)
    }

    const shadowStyle = {
        boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
        '&:hover': {
            boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.15)',
        }
    }

    const renderTrainingSetCard = (trainingSet, index) => {
        const inputFileCount = trainingSet.inputFiles?.length || 0
        const inputLabel = inputFileCount === 1 ? 'Input file' : 'Input files'

        return (
            <Card key={trainingSet.id || index} sx={{ 
                backgroundColor: '#FFFFFF', 
                borderRadius: '8px',
                mb: '16px',
                ...shadowStyle
            }}>
                <CardContent sx={{ padding: '16px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Input Files Section */}
                        <Box>
                            <Typography sx={{ 
                                fontSize: '14px', 
                                fontWeight: 500, 
                                color: '#374151', 
                                mb: 2 
                            }}>
                                {inputLabel} · {inputFileCount}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {trainingSet.inputFiles?.map((file, fileIndex) => (
                                    <Box
                                        key={fileIndex}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            padding: '8px 12px',
                                            backgroundColor: '#F9FAFB',
                                            borderRadius: '6px',
                                            border: '1px solid #E5E7EB',
                                        }}
                                    >
                                        <InsertDriveFileIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                        <Typography sx={{ fontSize: '12px', color: '#374151', flex: 1 }}>
                                            {formatFileName(file.name || file)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                onClick={() => handleViewFile(file)}
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    backgroundColor: 'transparent',
                                                    color: '#4F46E5',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                        backgroundColor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                View
                                            </Button>
                                            <Button
                                                onClick={() => handleReplaceFile(file)}
                                                sx={{
                                                    px: 1,
                                                    py: 0.5,
                                                    backgroundColor: 'transparent',
                                                    color: '#4F46E5',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    fontWeight: 500,
                                                    textTransform: 'none',
                                                    minWidth: 'auto',
                                                    '&:hover': {
                                                        backgroundColor: '#F3F4F6',
                                                    },
                                                }}
                                            >
                                                Replace
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Output File Section */}
                        <Box>
                            <Typography sx={{ 
                                fontSize: '14px', 
                                fontWeight: 500, 
                                color: '#374151', 
                                mb: 2 
                            }}>
                                Output file · 1
                            </Typography>
                            {trainingSet.outputFile && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        padding: '8px 12px',
                                        backgroundColor: '#F9FAFB',
                                        borderRadius: '6px',
                                        border: '1px solid #E5E7EB',
                                    }}
                                >
                                    <InsertDriveFileIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                    <Typography sx={{ fontSize: '12px', color: '#374151', flex: 1 }}>
                                        {formatFileName(trainingSet.outputFile.name || trainingSet.outputFile)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            onClick={() => handleViewFile(trainingSet.outputFile)}
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                backgroundColor: 'transparent',
                                                color: '#4F46E5',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: '#F3F4F6',
                                                },
                                            }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            onClick={() => handleReplaceFile(trainingSet.outputFile)}
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                backgroundColor: 'transparent',
                                                color: '#4F46E5',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    backgroundColor: '#F3F4F6',
                                                },
                                            }}
                                        >
                                            Replace
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Remove Training Set Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Button
                                onClick={() => handleRemoveTrainingSet(trainingSet.id)}
                                startIcon={<DeleteOutlineIcon sx={{ fontSize: '16px' }} />}
                                sx={{
                                    color: '#DC2626',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #F3F4F6',
                                    backgroundColor: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#FEF2F2',
                                        borderColor: '#FECACA',
                                    },
                                }}
                            >
                                Remove training set
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        )
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '548px',
                    height: '720px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                    padding: '24px',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: '24px',
                        left: '24px',
                        padding: '8px',
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: '#F3F4F6'
                        }
                    }}
                >
                    <CloseIcon sx={{ fontSize: '20px', color: '#6B7280' }} />
                </IconButton>

                {/* Modal Header */}
                <Box sx={{ mt: '48px', mb: '24px' }}>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#111827',
                            mb: '4px'
                        }}
                    >
                        {trainingSets?.length ? `{${trainingSets.length}} training sets` : 'No training sets'}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#6B7280',
                            lineHeight: 1.4
                        }}
                    >
                        Easily incorporate company-specific terminology, formatting, and structure into AI-generated drafts.{' '}
                        <Box
                            component="span"
                            onClick={handleLearnMore}
                            sx={{
                                color: '#4F46E5',
                                fontWeight: 500,
                                cursor: 'pointer',
                                textDecoration: 'underline'
                            }}
                        >
                            Learn more
                        </Box>
                    </Typography>
                </Box>

                {/* Content Card */}
                <Card
                    sx={{
                        flexGrow: 1,
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        mb: '112px', // Space for fixed save button (48px height + 48px padding + 16px margin)
                        overflow: 'hidden',
                        ...shadowStyle
                    }}
                >
                    <CardContent
                        sx={{
                            padding: '16px',
                            height: '100%',
                            overflow: 'auto'
                        }}
                    >
                        {trainingSets && trainingSets.length > 0 ? (
                            <Box>
                                {trainingSets.map((trainingSet, index) => 
                                    renderTrainingSetCard(trainingSet, index)
                                )}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    color: '#9CA3AF'
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 500
                                    }}
                                >
                                    No Training Data
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Fixed Save Button */}
                <Button
                    onClick={onSave}
                    sx={{
                        position: 'absolute',
                        bottom: '48px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '375px',
                        height: '48px',
                        backgroundColor: '#E5E7EB',
                        color: '#6B7280',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 500,
                        textTransform: 'none',
                        zIndex: 2,
                        '&:hover': {
                            backgroundColor: '#D1D5DB'
                        }
                    }}
                >
                    Save
                </Button>
            </Box>
        </Modal>
    )
}

export default TrainingSetModal 